import { createSlice, createAction, PayloadAction } from '@reduxjs/toolkit';
import { globalReset } from '@/store/actions';
import {
    VocabularySet,
    CreateVocabularySetRequest,
    UpdateVocabularySetRequest,
    VocabularySetFilters,
    VocabularySetResponse,
    PaginationParams,
} from '@/services/vocabularyService';

// === VOCABULARY SETS ACTIONS ===
export const fetchVocabularySetsRequest = createAction<{
    filters?: VocabularySetFilters;
    pagination?: PaginationParams;
}>('vocabularySets/fetchVocabularySetsRequest');
export const fetchVocabularySetsSuccess = createAction<VocabularySetResponse>(
    'vocabularySets/fetchVocabularySetsSuccess',
);
export const fetchVocabularySetsFailure = createAction<string>('vocabularySets/fetchVocabularySetsFailure');

export const fetchVocabularySetByIdRequest = createAction<string>('vocabularySets/fetchVocabularySetByIdRequest');
export const fetchVocabularySetByIdSuccess = createAction<VocabularySet>(
    'vocabularySets/fetchVocabularySetByIdSuccess',
);
export const fetchVocabularySetByIdFailure = createAction<string>('vocabularySets/fetchVocabularySetByIdFailure');

export const createVocabularySetRequest = createAction<CreateVocabularySetRequest>(
    'vocabularySets/createVocabularySetRequest',
);
export const createVocabularySetSuccess = createAction<VocabularySet>('vocabularySets/createVocabularySetSuccess');
export const createVocabularySetFailure = createAction<string>('vocabularySets/createVocabularySetFailure');

export const updateVocabularySetRequest = createAction<UpdateVocabularySetRequest>(
    'vocabularySets/updateVocabularySetRequest',
);
export const updateVocabularySetSuccess = createAction<VocabularySet>('vocabularySets/updateVocabularySetSuccess');
export const updateVocabularySetFailure = createAction<string>('vocabularySets/updateVocabularySetFailure');

export const deleteVocabularySetRequest = createAction<string>('vocabularySets/deleteVocabularySetRequest');
export const deleteVocabularySetSuccess = createAction<string>('vocabularySets/deleteVocabularySetSuccess');
export const deleteVocabularySetFailure = createAction<string>('vocabularySets/deleteVocabularySetFailure');

// === STATE INTERFACE ===
export interface VocabularySetsState {
    vocabularySets: VocabularySet[];
    selectedVocabularySet: VocabularySet | null;

    // Filters
    vocabularySetFilters: VocabularySetFilters;

    // Loading States
    isLoadingSets: boolean;
    isCreatingSet: boolean;
    isUpdatingSet: boolean;
    isDeletingSet: boolean;

    // Error States
    setError: string | null;
}

const initialState: VocabularySetsState = {
    vocabularySets: [],
    selectedVocabularySet: null,
    vocabularySetFilters: {},
    isLoadingSets: false,
    isCreatingSet: false,
    isUpdatingSet: false,
    isDeletingSet: false,
    setError: null,
};

const vocabularySetsSlice = createSlice({
    name: 'vocabularySets',
    initialState,
    reducers: {
        setVocabularySetFilters: (state, action: PayloadAction<VocabularySetFilters>) => {
            state.vocabularySetFilters = action.payload;
        },
        clearSetFilters: (state) => {
            state.vocabularySetFilters = {};
        },
        setSelectedVocabularySet: (state, action: PayloadAction<VocabularySet | null>) => {
            state.selectedVocabularySet = action.payload;
        },
        clearSetError: (state) => {
            state.setError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(globalReset, () => initialState)

            // === FETCH VOCABULARY SETS ===
            .addCase(fetchVocabularySetsRequest, (state) => {
                state.isLoadingSets = true;
                state.setError = null;
            })
            .addCase(fetchVocabularySetsSuccess, (state, action) => {
                state.isLoadingSets = false;
                state.vocabularySets = action.payload.results;
            })
            .addCase(fetchVocabularySetsFailure, (state, action) => {
                state.isLoadingSets = false;
                state.setError = action.payload;
            })

            // === FETCH VOCABULARY SET BY ID ===
            .addCase(fetchVocabularySetByIdRequest, (state) => {
                state.isLoadingSets = true;
                state.setError = null;
            })
            .addCase(fetchVocabularySetByIdSuccess, (state, action) => {
                state.isLoadingSets = false;
                state.selectedVocabularySet = action.payload;
                // Update set in sets array if it exists
                const index = state.vocabularySets.findIndex((set) => set.id === action.payload.id);
                if (index !== -1) {
                    state.vocabularySets[index] = action.payload;
                }
            })
            .addCase(fetchVocabularySetByIdFailure, (state, action) => {
                state.isLoadingSets = false;
                state.setError = action.payload;
            })

            // === CREATE VOCABULARY SET ===
            .addCase(createVocabularySetRequest, (state) => {
                state.isCreatingSet = true;
                state.setError = null;
            })
            .addCase(createVocabularySetSuccess, (state, action) => {
                state.isCreatingSet = false;
                state.vocabularySets.unshift(action.payload);
            })
            .addCase(createVocabularySetFailure, (state, action) => {
                state.isCreatingSet = false;
                state.setError = action.payload;
            })

            // === UPDATE VOCABULARY SET ===
            .addCase(updateVocabularySetRequest, (state) => {
                state.isUpdatingSet = true;
                state.setError = null;
            })
            .addCase(updateVocabularySetSuccess, (state, action) => {
                state.isUpdatingSet = false;
                const index = state.vocabularySets.findIndex((set) => set.id === action.payload.id);
                if (index !== -1) {
                    state.vocabularySets[index] = action.payload;
                }
                if (state.selectedVocabularySet?.id === action.payload.id) {
                    state.selectedVocabularySet = action.payload;
                }
            })
            .addCase(updateVocabularySetFailure, (state, action) => {
                state.isUpdatingSet = false;
                state.setError = action.payload;
            })

            // === DELETE VOCABULARY SET ===
            .addCase(deleteVocabularySetRequest, (state) => {
                state.isDeletingSet = true;
                state.setError = null;
            })
            .addCase(deleteVocabularySetSuccess, (state, action) => {
                state.isDeletingSet = false;
                state.vocabularySets = state.vocabularySets.filter((set) => set.id !== action.payload);
                if (state.selectedVocabularySet?.id === action.payload) {
                    state.selectedVocabularySet = null;
                }
            })
            .addCase(deleteVocabularySetFailure, (state, action) => {
                state.isDeletingSet = false;
                state.setError = action.payload;
            });
    },
});

export const { setVocabularySetFilters, clearSetFilters, setSelectedVocabularySet, clearSetError } =
    vocabularySetsSlice.actions;

export default vocabularySetsSlice.reducer;
